<template>
  <b-card class="mb-4 project-state-card">
    <b-card-title>{{ $t('projectState') }}</b-card-title>
    <b-card-text class="project-state-card-grid-container">
      <div>{{ $t('projectState') }}:</div>
      <div>
        <b-badge :variant="getProjectStateColor(project.projectState)">
          {{ $t(project.projectState) }}
        </b-badge>
      </div>

      <template v-if="project.deferredState && project.deferredState.deferred === 'YES'">
        <div>{{ $t('deferredState') }}:</div>
        <div>
          <b-badge
            id="popover-deferredState"
            :variant="yesNoStateReversedColor(project.deferredState.deferred)"
          >
            {{ $t(project.deferredState.deferred) }}
          </b-badge>
          <b-popover target="popover-deferredState" triggers="hover" placement="right">
            <template #title>{{ $t('deferredState') }}</template>
            <div class="popover-grid-container2">
              <div>
                <strong>{{ $t('reason') }}</strong>
              </div>
              <div>{{ $t(project.deferredState.reason) }}</div>
              <div>
                <strong>{{ $t('description') }}</strong>
              </div>
              <div>{{ project.deferredState.description }}</div>
              <div>
                <strong>{{ $t('comment') }}</strong>
              </div>
              <div>{{ project.deferredState.comment }}</div>
            </div>
          </b-popover>
        </div>
      </template>

      <div>
        {{ $t('requirements') }} <strong>{{ $t('netRegistration_short') }}</strong>
        {{ $t('fullfilled') }}:
      </div>
      <div>
        <b-badge
          id="popover-netRegistrationRequirements"
          :variant="getYesNoStateColor(project.netRegistrationRequirementsFullfilledState)"
        >
          {{ $t(project.netRegistrationRequirementsFullfilledState) }}
        </b-badge>
        <b-popover target="popover-netRegistrationRequirements" triggers="hover" placement="right">
          <template #title>{{ $t('netRegistrationRequirements') }}</template>
          <div
            class="popover-grid-container"
            v-for="(requirement, index) in project.netRegistrationRequirements"
            :key="index"
          >
            <div>{{ $t(requirement.name) }}:</div>
            <div>
              <b-badge :variant="getYesNoStateColor(requirement.fullfilledState)">
                {{ $t(requirement.fullfilledState) }}
              </b-badge>
            </div>
          </div>
        </b-popover>
      </div>
      <div>
        {{ $t('requirements') }} <strong>{{ $t('operationRegistration_short') }}</strong>
        {{ $t('fullfilled') }}:
      </div>
      <div class="">
        <b-badge
          id="popover-operationRegistrationRequirements"
          :variant="getYesNoStateColor(project.operationRegistrationRequirementsFullfilledState)"
        >
          {{ $t(project.operationRegistrationRequirementsFullfilledState) }}
        </b-badge>
        <b-popover
          target="popover-operationRegistrationRequirements"
          triggers="hover"
          placement="right"
        >
          <template #title>{{ $t('operationRegistrationRequirements') }}</template>
          <div
            class="popover-grid-container"
            v-for="(requirement, index) in project.operationRegistrationRequirements"
            :key="index"
          >
            <div>{{ $t(requirement.name) }}:</div>
            <div>
              <b-badge :variant="getYesNoStateColor(requirement.fullfilledState)">
                {{ $t(requirement.fullfilledState) }}
              </b-badge>
            </div>
          </div>
        </b-popover>
      </div>
    </b-card-text>

    <!-- Audit Section -->
    <b-card-text class="mt-3 pt-3 border-top">
      <div class="audit-grid-container">
        <div :class="{ 'text-danger': isOutdated }">
          {{ $t('editing_state') }}
          <i v-if="isOutdated" class="fa fa-exclamation ml-1"></i>
        </div>
        <div>
          <span v-if="isOutdated">
            {{ $t('project_outdated') }} <br />
            {{ $t('please_reload') }}:
            <i @click="refreshPage" class="fa fa-lg fa-refresh ml-1 reload"></i>
          </span>
          <b-badge v-else :variant="getYesNoStateColor('YES')">
            OK
          </b-badge>
        </div>

        <template
          v-if="
            projectEditingState &&
              projectEditingState.recentProjectUsers &&
              projectEditingState.recentProjectUsers.length > 0
          "
        >
          <div>{{ $t('current_users') }}:</div>
          <div>
            <ul class="recent-users-list">
              <li v-for="audit in projectEditingState.recentProjectUsers" :key="audit._id">
                {{ $time(audit.createdAt) }}: {{ audit.user.firstname }} {{ audit.user.lastname }}
              </li>
            </ul>
          </div>
        </template>
      </div>
    </b-card-text>
  </b-card>
</template>

<script>
import {
  getYesNoStateColor,
  getProjectStateColor,
  yesNoStateReversedColor,
} from '@/helpers/colors';
import { mapActions } from 'vuex';

export default {
  props: {
    project: { type: Object },
  },
  data() {
    return {
      projectEditingState: null,
      pollState: null,
      intervalInMilliseconds: 30000,
    };
  },
  computed: {
    isOutdated() {
      if (!this.projectEditingState) return false;
      return this.projectEditingState.projectIsOutdated;
    },
  },
  methods: {
    ...mapActions(['fetchProjectEditingState']),

    refreshPage() {
      location.reload();
    },

    async pollIsOutdated() {
      this.pollState = setInterval(async () => {
        this.projectEditingState = await this.fetchProjectEditingState(this.project.number);
      }, this.intervalInMilliseconds);
    },

    stopPolling() {
      clearInterval(this.pollState);
    },

    getYesNoStateColor,
    getProjectStateColor,
    yesNoStateReversedColor,
  },

  async mounted() {
    if (this.project.number && this.project.number !== '_new') {
      this.projectEditingState = await this.fetchProjectEditingState(this.project.number);
      await this.pollIsOutdated();
    }
  },

  beforeDestroy() {
    this.stopPolling();
  },
};
</script>

<style scoped lang="scss">
.project-state-card {
  max-width: 30rem;
}
.project-state-card-grid-container {
  display: grid;
  grid-template-columns: 200px 1fr;
  column-gap: 1em;
  row-gap: 0.5em;
}

.audit-grid-container {
  display: grid;
  grid-template-columns: 200px 1fr;
  column-gap: 1em;
  row-gap: 0.5em;

  
}
.popover-grid-container {
  display: grid;
  grid-template-columns: 4fr 1fr;
}
.popover-grid-container div {
  padding: 0.2em;
}
.popover-grid-container2 {
  display: grid;
  grid-template-columns: 1fr 4fr;
}
.popover-grid-container2 div {
  padding: 0.2em;
}

.recent-users-list {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
}

i.reload {
  color: $danger;
  cursor: pointer;
}
</style>
